import { Children, isValidElement, type ReactNode } from "react";

/**
 * Post-processes Scout answer markdown into chip-friendly data.
 *
 * Scout emits citations as inline markdown links whose `title` attribute is
 * `"cite"` (e.g. `[40 CFR 370.10](https://... "cite")`). This module:
 *   - Detects those links, rewrites their title to an internal
 *     `SCOUT_CITE:<iconKind>` marker so `MessageContent`'s `a` component can
 *     render them as chips, and
 *   - Collects the same links into a structured `ScoutCitationSource[]` that
 *     the caller renders as a collapsible Sources accordion below the answer.
 *
 * URL-less citations use `#` (or empty href) — those render as a non-clickable
 * book-icon chip and appear in the accordion with no link.
 *
 * A legacy fallback also parses the older `[N]` + `## Sources` block format so
 * historical conversations still render correctly. Legacy parsing only runs
 * when no inline `title="cite"` links are found.
 */

export type CitationIconKind = "regulation" | "link" | "book";

export const CITATION_LINK_TITLE_PREFIX = "SCOUT_CITE:";
export const CITATION_NO_URL_HREF = "#scout-citation";

export interface ScoutCitationSource {
  number: number;
  title: string;
  url?: string;
  iconKind: CitationIconKind;
}

/**
 * Detects CFR-style titles like "40 CFR 370.10" or "40 CFR Part 370" and state
 * regulation citations like "NY ECL 37-0103" / "CA HSC 25500" / "CA CCR ...".
 */
const REGULATION_TITLE_PATTERN = /(\b\d+\s*CFR\b|\b(?:[A-Z]{2})\s+(?:ECL|HSC|CCR|NYCRR)\b)/;

/**
 * Matches Scout inline citation links: `[label](href "cite")`. The `"cite"`
 * title is the marker the model attaches to distinguish citations from
 * ordinary markdown links (e.g. portal navigation links).
 *
 * - Label: any non-`]` characters, non-greedy
 * - Href: any non-whitespace characters, with balanced parentheses such as an
 *   eCFR paragraph anchor's (may be `#` for URL-less)
 * - Title: literal `"cite"` (case-insensitive, allows a trailing modifier)
 */
const CITATION_LINK_PATTERN = /\[([^\]]+?)\]\(((?:[^\s()]|\([^\s()]*\))*)\s+"cite[^"]*"\)/gi;

export function pickIconKind(source: { title: string; url?: string }): CitationIconKind {
  if (REGULATION_TITLE_PATTERN.test(source.title)) {
    return "regulation";
  }
  if (source.url) {
    return "link";
  }
  return "book";
}

function normalizeHref(href: string): string | undefined {
  const trimmed = href.trim();
  if (trimmed === "" || trimmed === "#") {
    return undefined;
  }
  return trimmed;
}

function escapeMarkdownLinkLabel(label: string): string {
  return label.replace(/[[\]]/g, "");
}

export interface CitationTransformResult {
  /**
   * Markdown body with citation links rewritten to carry the internal chip
   * marker in their `title` attribute so the `a` component can render them as
   * chips.
   */
  markdown: string;
  /**
   * Parsed sources, or null when no citations were found. Callers render this
   * as a collapsible accordion below the answer.
   */
  sources: ScoutCitationSource[] | null;
}

interface CollectedSource {
  number: number;
  title: string;
  url?: string;
  iconKind: CitationIconKind;
}

function transformInlineCitationLinks(markdown: string): {
  markdown: string;
  sources: CollectedSource[];
} {
  const byKey = new Map<string, CollectedSource>();
  let nextNumber = 1;

  const rewritten = markdown.replace(CITATION_LINK_PATTERN, (_match, rawLabel, rawHref) => {
    const label = String(rawLabel).trim();
    const url = normalizeHref(String(rawHref ?? ""));
    const iconKind = pickIconKind({ title: label, url });
    const dedupKey = url ?? `label:${label.toLowerCase()}`;

    let source = byKey.get(dedupKey);
    if (!source) {
      source = { number: nextNumber, title: label, url, iconKind };
      byKey.set(dedupKey, source);
      nextNumber += 1;
    }

    const href = url ?? CITATION_NO_URL_HREF;
    const safeLabel = escapeMarkdownLinkLabel(label);
    return `[${safeLabel}](${href} "${CITATION_LINK_TITLE_PREFIX}${iconKind}")`;
  });

  return { markdown: rewritten, sources: Array.from(byKey.values()) };
}

/** Inline `"cite"` links rewritten to chip syntax, without the legacy fallback. */
export function transformInlineCitations(markdown: string): string {
  return transformInlineCitationLinks(markdown).markdown;
}

// ─── Legacy [N] + `## Sources` block fallback ───────────────────────────────
// Older Scout outputs used numbered footnote markers with a trailing
// `## Sources` block. Kept for backward compatibility on historical
// conversations. Only runs when no inline `title="cite"` links are present.

interface LegacyParsedSource {
  number: number;
  title: string;
  url?: string;
}

const LEGACY_SOURCE_LINE_PATTERN = /^\[(\d+)\]\s*(.*)$/;
const LEGACY_SOURCE_ENTRY_SEPARATOR = /\s+[—–-]\s+/;

function findSourcesHeadingIndex(markdown: string): number {
  const pattern = /(^|\n)##\s+Sources\s*(\n|$)/i;
  const match = pattern.exec(markdown);
  if (!match) {
    return -1;
  }
  return match.index + (match[1] === "\n" ? 1 : 0);
}

function parseLegacySourcesBlock(block: string): LegacyParsedSource[] {
  const lines = block.split("\n");
  const sources: LegacyParsedSource[] = [];
  let current: LegacyParsedSource | null = null;

  for (const rawLine of lines) {
    const line = rawLine.trim();
    if (line.length === 0 || line.startsWith("##")) {
      continue;
    }

    const numberMatch = LEGACY_SOURCE_LINE_PATTERN.exec(line);
    if (numberMatch) {
      if (current) sources.push(current);
      const number = Number(numberMatch[1]);
      const rest = numberMatch[2] ?? "";
      const parts = rest.split(LEGACY_SOURCE_ENTRY_SEPARATOR).map((part) => part.trim());
      const title = parts[0] ?? "";
      let url: string | undefined;
      for (let i = 1; i < parts.length; i += 1) {
        const part = parts[i];
        if (!part) continue;
        if (!url && /^https?:\/\//i.test(part)) {
          url = part;
          break;
        }
      }
      current = { number, title, url };
    }
  }

  if (current) sources.push(current);
  return sources;
}

function transformLegacyCitations(markdown: string): CitationTransformResult {
  const headingIndex = findSourcesHeadingIndex(markdown);
  if (headingIndex === -1) {
    return { markdown, sources: null };
  }

  const body = markdown.slice(0, headingIndex);
  const sourcesBlock = markdown.slice(headingIndex);
  const parsed = parseLegacySourcesBlock(sourcesBlock);
  if (parsed.length === 0) {
    return { markdown, sources: null };
  }

  const byNumber = new Map<number, LegacyParsedSource>();
  for (const source of parsed) {
    byNumber.set(source.number, source);
  }

  const rewrittenBody = body
    .replace(/\[(\d+)\]/g, (match, digits: string) => {
      const source = byNumber.get(Number(digits));
      if (!source) return match;
      const iconKind = pickIconKind(source);
      const href = source.url ?? CITATION_NO_URL_HREF;
      const label = escapeMarkdownLinkLabel(source.title);
      return `[${label}](${href} "${CITATION_LINK_TITLE_PREFIX}${iconKind}")`;
    })
    .replace(/\s+$/g, "");

  const sources: ScoutCitationSource[] = parsed.map((source, index) => ({
    number: index + 1,
    title: source.title,
    url: source.url,
    iconKind: pickIconKind(source),
  }));

  return { markdown: rewrittenBody, sources };
}

export function transformCitations(markdown: string): CitationTransformResult {
  const { markdown: rewritten, sources } = transformInlineCitationLinks(markdown);
  if (sources.length > 0) {
    // If the model wrote its own trailing `## Sources` block despite the
    // prompt saying not to, strip it. The accordion renders sources parsed
    // from inline links instead.
    const headingIndex = findSourcesHeadingIndex(rewritten);
    const body =
      headingIndex === -1 ? rewritten : rewritten.slice(0, headingIndex).replace(/\s+$/g, "");
    return {
      markdown: body,
      sources: sources.map((source, index) => ({ ...source, number: index + 1 })),
    };
  }
  return transformLegacyCitations(markdown);
}

export function parseCitationChipTitle(title: string | undefined): CitationIconKind | null {
  if (!title || !title.startsWith(CITATION_LINK_TITLE_PREFIX)) {
    return null;
  }
  const kind = title.slice(CITATION_LINK_TITLE_PREFIX.length);
  if (kind === "regulation" || kind === "link" || kind === "book") {
    return kind;
  }
  return null;
}

/** A link's visible text, for a chip label. */
export function extractChildrenText(children: ReactNode): string {
  let text = "";
  Children.forEach(children, (child) => {
    if (typeof child === "string" || typeof child === "number") {
      text += String(child);
    } else if (isValidElement<{ children?: ReactNode }>(child)) {
      text += extractChildrenText(child.props.children);
    }
  });
  return text;
}
